/**
 * Confetti — Lazy-loaded celebration effect.
 *
 * Fires a canvas-confetti burst on demand. Loaded via dynamic import
 * so it's never in the critical path (~3 KB, loaded on trigger only).
 *
 * Automatically disabled when user prefers reduced motion.
 *
 * @see documents/ui-enhancement-spec.md §16
 */

import type { ConfettiIntensity } from '../../theme';
import { CONFETTI } from '../../theme';

/**
 * Fire a confetti burst. Lazy-loads canvas-confetti on first call.
 *
 * @param intensity - 'light' | 'medium' | 'full' (default: 'medium')
 * @param origin - { x, y } normalised origin (default: center-top)
 */
export async function fireConfetti(
  intensity: ConfettiIntensity = 'medium',
  origin?: { x: number; y: number },
): Promise<void> {
  // Respect reduced motion
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  try {
    const confettiModule = await import('canvas-confetti');
    const confetti = confettiModule.default;

    const config = CONFETTI[intensity];

    confetti({
      particleCount: config.particles,
      colors: [...config.colors],
      gravity: config.gravity,
      origin: origin ?? { x: 0.5, y: 0.35 },
      spread: 70,
      startVelocity: 30,
      ticks: 60,
      disableForReducedMotion: config.disableForReducedMotion,
    });
  } catch {
    // Confetti should never break the UI
    console.warn('[zelium] Failed to load confetti');
  }
}
